import React, { useState, useMemo, useEffect, useCallback } from "react";

import {
  reportError,
  trackEvent,
  NotificationStatuses,
  BLACK_BOX_ELEMENT_ID,
} from "@jsainsburyplc/id-ui-common";

import { otpService } from "services/otp.service";
import { getErrorMessage } from "utils/otpForm";
import { categoryTypes, actionTypes } from "constants/tracking";
import {
  EMAIL_PREFERENCE_VALUE,
  MOBILE_PREFERENCE_VALUE,
  DATA_CUSTOMER_INFO,
  DATA_ERROR,
} from "constants/login";
import {
  newCodeMessage,
  newCodeErrorMessage,
  OTP_FORM_ID,
  MFA_CODE_SENT,
} from "constants/mfaAndOtp";
import { getBlackBoxToken } from "utils/iovation";
import { isIovationEnabled } from "utils/env";

import OtpForm from "components/OtpForm";
import PageFormat from "components/PageFormat";

const parseCustomerInfo = (customerInfoValue) => {
  const defaultCustomerInfo = { email: "", mobile: "" };
  try {
    return (
      JSON.parse(decodeURIComponent(customerInfoValue)) || defaultCustomerInfo
    );
  } catch (e) {
    return defaultCustomerInfo;
  }
};

const trackRequestCode = (action) => {
  trackEvent({
    category: categoryTypes.MFA,
    label: "Request new MFA code",
    action,
  });
};

const trackFormSubmit = () => {
  trackEvent({
    category: categoryTypes.MFA,
    action: actionTypes.SUBMIT,
    label: "Submit OTP code",
  });
};

const trackFormSubmitError = (code) => {
  trackEvent({
    category: categoryTypes.MFA,
    action: actionTypes.ERROR,
    label: `Submit OTP code: ${code}`,
  });
};

const getAndAssignBlackboxToken = (form) => {
  try {
    form[BLACK_BOX_ELEMENT_ID].value = getBlackBoxToken(OTP_FORM_ID);
  } catch {
    reportError(`${OTP_FORM_ID} - the blackbox form field is not present`);
  }
};

const OtpPage = () => {
  const rootElem = document.querySelector("#root");
  const customerInfoValue = rootElem.getAttribute(DATA_CUSTOMER_INFO);
  const errorCode = parseInt(rootElem.getAttribute(DATA_ERROR));

  const [requestingNewCode, setRequestingNewCode] = useState(false);
  const [message, setMessage] = useState(
    errorCode ? getErrorMessage(errorCode) : undefined
  );

  const parsedCustomerInfo = useMemo(
    () => parseCustomerInfo(customerInfoValue),
    [customerInfoValue]
  );

  const preference = !parsedCustomerInfo.mobile
    ? EMAIL_PREFERENCE_VALUE
    : MOBILE_PREFERENCE_VALUE;

  const preferenceInfo =
    preference === MOBILE_PREFERENCE_VALUE
      ? parsedCustomerInfo.mobile
      : parsedCustomerInfo.email;

  const submitHandler = () => {
    const form = document.getElementById(OTP_FORM_ID);
    isIovationEnabled() && getAndAssignBlackboxToken(form);
    trackFormSubmit();
    form.submit();
  };

  const requestCode = useCallback(async (showSuccessMessage) => {
    setRequestingNewCode(true);
    try {
      const response = await otpService.requestOtp();
      if (response.ok) {
        sessionStorage.setItem(MFA_CODE_SENT, "true");
        setRequestingNewCode(false);
        trackRequestCode(actionTypes.SUCCESS);
        if (showSuccessMessage) {
          setMessage({
            type: NotificationStatuses.SUCCESS_TYPE,
            text: newCodeMessage,
          });
        }
      } else {
        throw Error;
      }
    } catch (error) {
      setRequestingNewCode(false);
      trackRequestCode(actionTypes.ERROR);
      setMessage({
        type: NotificationStatuses.ERROR_TYPE,
        text: newCodeErrorMessage,
      });
    }
  }, []);

  const handleRequestNewCode = async (event) => {
    event && event.preventDefault();
    trackRequestCode(actionTypes.CODE_REQUESTED);
    requestCode(true);
  };

  useEffect(() => {
    const isCodeSent = sessionStorage.getItem(MFA_CODE_SENT) === "true";
    !isCodeSent && requestCode();
  }, [requestCode]);

  useEffect(() => {
    if (message) {
      trackFormSubmitError(errorCode);
    }
  }, [message, errorCode]);

  return (
    <PageFormat>
      <div className="c-hero c-hero--center">
        <h1>We've sent you a code</h1>
      </div>
      <p className="ln-u-margin-bottom*3">
        We just need to verify your details. We've sent a verification code to:
      </p>
      <p className="ln-u-margin-bottom*3 ln-u-font-weight-bold">
        {preferenceInfo}
      </p>
      <p>Please enter the code to continue to log in:</p>

      <OtpForm
        onRequestCode={handleRequestNewCode}
        onSubmit={submitHandler}
        isRequestingCode={requestingNewCode}
        notification={message}
        isIovationEnabled={process.env.REACT_APP_IOVATION_ENABLED}
      />
    </PageFormat>
  );
};

export default OtpPage;
